import React, { useCallback, useEffect, useRef, useState } from 'react';
import { barrierAPI, camerasAPI, notifyNotificationsChanged, type BarrierOpenOn } from '../../utils/api';
import {
  DirectionVector,
  drawDirectionArrow,
  parseDirectionVector,
} from '../../utils/directionVector';
import '../RoiSelector/RoiSelector.css';
import './DirectionVectorSelector.css';

const vectorIconSvgUrl = `${process.env.PUBLIC_URL}/icons/svg_icon/direction-vector.svg`;
const btnIcon = {
  save: `${process.env.PUBLIC_URL}/icons/svg_icon/save.svg`,
  clear: `${process.env.PUBLIC_URL}/icons/svg_icon/clear.svg`,
  sbros: `${process.env.PUBLIC_URL}/icons/svg_icon/sbros.svg`,
  cancel: `${process.env.PUBLIC_URL}/icons/svg_icon/cancel.svg`,
} as const;

interface DirectionVectorSelectorProps {
  camera: {
    camera_id: string | null;
    webrtc_out?: string | null;
    status?: any;
  };
  /** Уже сохранённый вектор (проценты 0–100), если известен. */
  currentVector?: DirectionVector | null;
  onClose: () => void;
  /** Вызывается после успешного сохранения/сброса; null — вектор сброшен. */
  onUpdated?: (vector: DirectionVector | null) => void;
}

/** Точки в координатах canvas. */
interface LineState {
  isDrawing: boolean;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  line: { x1: number; y1: number; x2: number; y2: number } | null;
}

const MIN_LINE_LENGTH_PX = 10;

const DirectionVectorSelector: React.FC<DirectionVectorSelectorProps> = ({
  camera,
  currentVector = null,
  onClose,
  onUpdated,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [lineState, setLineState] = useState<LineState>({
    isDrawing: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    line: null,
  });
  const [savedVector, setSavedVector] = useState<DirectionVector | null>(currentVector);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | 'loading' | null; message: string }>({
    type: null,
    message: '',
  });
  // Шлагбаум с open_on=entry/exit держится на векторе: сбрасывать его нельзя (внешний API отдаст 409)
  const [barrierOpenOn, setBarrierOpenOn] = useState<BarrierOpenOn | null>(null);
  const [videoWidth, setVideoWidth] = useState<number>(1920);
  const [videoHeight, setVideoHeight] = useState<number>(1080);
  const [canvasVersion, setCanvasVersion] = useState(0);

  // Размеры видео из status камеры
  useEffect(() => {
    const statusData = camera.status;
    if (!statusData) return;
    if (statusData.resolution?.width && statusData.resolution?.height) {
      setVideoWidth(statusData.resolution.width);
      setVideoHeight(statusData.resolution.height);
    } else if (statusData.src_width && statusData.src_height) {
      setVideoWidth(statusData.src_width);
      setVideoHeight(statusData.src_height);
    } else if (statusData.width && statusData.height) {
      setVideoWidth(statusData.width);
      setVideoHeight(statusData.height);
    }
  }, [camera.status]);

  // Подтягиваем актуальный вектор с сервера (на случай, если родитель его не передал)
  useEffect(() => {
    if (!camera.camera_id) return;
    let cancelled = false;
    camerasAPI
      .getDirectionVector(camera.camera_id)
      .then((res) => {
        if (!cancelled && res.success) {
          setSavedVector(parseDirectionVector(res.vector, videoWidth, videoHeight));
        }
      })
      .catch(() => {
        /* оставляем то, что передал родитель */
      });
    return () => {
      cancelled = true;
    };
  }, [camera.camera_id, videoWidth, videoHeight]);

  // Узнаём режим шлагбаума, чтобы не дать сбросить вектор, на котором он держится
  useEffect(() => {
    if (!camera.camera_id) return;
    let cancelled = false;
    barrierAPI
      .get(camera.camera_id)
      .then((res) => {
        if (!cancelled) {
          setBarrierOpenOn(res.success && res.barrier?.enabled ? res.barrier.open_on : null);
        }
      })
      .catch(() => {
        if (!cancelled) setBarrierOpenOn(null);
      });
    return () => {
      cancelled = true;
    };
  }, [camera.camera_id]);

  /** Область, которую реально занимает видео внутри canvas (letterbox/pillarbox). */
  const getVideoRect = useCallback(
    (canvas: HTMLCanvasElement) => {
      const videoAspect = videoWidth / videoHeight;
      const canvasAspect = canvas.width / canvas.height;
      let w: number, h: number, x: number, y: number;
      if (canvasAspect > videoAspect) {
        h = canvas.height;
        w = canvas.height * videoAspect;
        x = (canvas.width - w) / 2;
        y = 0;
      } else {
        w = canvas.width;
        h = canvas.width / videoAspect;
        x = 0;
        y = (canvas.height - h) / 2;
      }
      return { x, y, w, h };
    },
    [videoWidth, videoHeight]
  );

  /** Проценты (0–100) → координаты canvas. */
  const percentToCanvas = useCallback(
    (px: number, py: number, canvas: HTMLCanvasElement) => {
      const r = getVideoRect(canvas);
      return { x: r.x + (px / 100) * r.w, y: r.y + (py / 100) * r.h };
    },
    [getVideoRect]
  );

  /** Координаты canvas → пиксели исходного кадра (с обрезкой по границам). */
  const canvasToVideo = useCallback(
    (cx: number, cy: number, canvas: HTMLCanvasElement) => {
      const r = getVideoRect(canvas);
      const x = ((cx - r.x) / r.w) * videoWidth;
      const y = ((cy - r.y) / r.h) * videoHeight;
      return {
        x: Math.max(0, Math.min(videoWidth, Math.round(x))),
        y: Math.max(0, Math.min(videoHeight, Math.round(y))),
      };
    },
    [getVideoRect, videoWidth, videoHeight]
  );

  // Инициализация iframe, canvas и pointer-событий
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas || !camera.webrtc_out || !camera.camera_id) return;

    let streamUrl = camera.webrtc_out;
    if (streamUrl.startsWith('https://') && streamUrl.includes('192.168.')) {
      streamUrl = streamUrl.replace('https://', 'http://');
    }

    const iframe = document.createElement('iframe');
    iframe.src = streamUrl;
    iframe.style.width = '100%';
    iframe.style.aspectRatio = '16 / 9';
    iframe.style.border = 'none';
    iframe.style.borderRadius = '8px';
    iframe.allow = 'autoplay; fullscreen';
    iframe.setAttribute('allowfullscreen', 'true');
    iframeRef.current = iframe;
    container.appendChild(iframe);

    const updateCanvasSize = () => {
      const rect = iframe.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      if (canvas.width === rect.width && canvas.height === rect.height) return;
      canvas.width = rect.width;
      canvas.height = rect.height;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
      // Размер поменялся — сбрасываем нарисованную линию, перерисуем сохранённый вектор
      setLineState((prev) => ({ ...prev, isDrawing: false, line: null }));
      setCanvasVersion((v) => v + 1);
    };

    iframe.addEventListener('load', () => {
      setTimeout(updateCanvasSize, 100);
      setTimeout(updateCanvasSize, 500);
      setTimeout(updateCanvasSize, 1000);
    });
    // На случай, если iframe уже имеет размер до события load
    setTimeout(updateCanvasSize, 0);

    window.addEventListener('resize', updateCanvasSize);
    window.addEventListener('orientationchange', updateCanvasSize);

    const getPoint = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const handlePointerDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const { x, y } = getPoint(e);
      setLineState({ isDrawing: true, startX: x, startY: y, currentX: x, currentY: y, line: null });
    };

    const handlePointerMove = (e: PointerEvent) => {
      e.preventDefault();
      setLineState((prev) => {
        if (!prev.isDrawing) return prev;
        const { x, y } = getPoint(e);
        return { ...prev, currentX: x, currentY: y };
      });
    };

    const handlePointerUp = (e: PointerEvent) => {
      try {
        if (canvas.hasPointerCapture(e.pointerId)) {
          canvas.releasePointerCapture(e.pointerId);
        }
      } catch {
        /* ignore */
      }
      setLineState((prev) => {
        if (!prev.isDrawing) return prev;
        const length = Math.hypot(prev.currentX - prev.startX, prev.currentY - prev.startY);
        if (length < MIN_LINE_LENGTH_PX) {
          return { ...prev, isDrawing: false, line: null };
        }
        return {
          ...prev,
          isDrawing: false,
          line: { x1: prev.startX, y1: prev.startY, x2: prev.currentX, y2: prev.currentY },
        };
      });
    };

    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('pointerup', handlePointerUp);
    canvas.addEventListener('pointercancel', handlePointerUp);
    canvas.addEventListener('lostpointercapture', handlePointerUp);

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      window.removeEventListener('orientationchange', updateCanvasSize);
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerup', handlePointerUp);
      canvas.removeEventListener('pointercancel', handlePointerUp);
      canvas.removeEventListener('lostpointercapture', handlePointerUp);
      if (iframeRef.current && iframeRef.current.parentNode) {
        iframeRef.current.parentNode.removeChild(iframeRef.current);
      }
      iframeRef.current = null;
    };
  }, [camera.webrtc_out, camera.camera_id]);

  // Перерисовка: рисуемая линия имеет приоритет, иначе — сохранённый вектор
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0 || canvas.height === 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (lineState.isDrawing) {
      drawDirectionArrow(ctx, lineState.startX, lineState.startY, lineState.currentX, lineState.currentY);
      return;
    }
    if (lineState.line) {
      const { x1, y1, x2, y2 } = lineState.line;
      drawDirectionArrow(ctx, x1, y1, x2, y2, { label: 'Новый вектор' });
      return;
    }
    if (savedVector) {
      const a = percentToCanvas(savedVector.x1, savedVector.y1, canvas);
      const b = percentToCanvas(savedVector.x2, savedVector.y2, canvas);
      drawDirectionArrow(ctx, a.x, a.y, b.x, b.y, { label: 'Текущий вектор' });
    }
  }, [lineState, savedVector, percentToCanvas, canvasVersion]);

  const finish = (vector: DirectionVector | null) => {
    setTimeout(() => {
      if (onUpdated) onUpdated(vector);
      onClose();
    }, 800);
  };

  const handleSave = async () => {
    const canvas = canvasRef.current;
    if (!lineState.line || !camera.camera_id || !canvas) return;

    setStatus({ type: 'loading', message: 'Отправка данных...' });

    try {
      const a = canvasToVideo(lineState.line.x1, lineState.line.y1, canvas);
      const b = canvasToVideo(lineState.line.x2, lineState.line.y2, canvas);

      if (a.x === b.x && a.y === b.y) {
        setStatus({ type: 'error', message: 'Начало и конец вектора не должны совпадать' });
        return;
      }

      const res = await camerasAPI.setDirectionVector(camera.camera_id, {
        x1: a.x,
        y1: a.y,
        x2: b.x,
        y2: b.y,
        preview_width: videoWidth,
        preview_height: videoHeight,
      });

      // Для отрисовки на превью — сразу в процентах, как отдаёт GET
      const vector: DirectionVector = {
        x1: (a.x / videoWidth) * 100,
        y1: (a.y / videoHeight) * 100,
        x2: (b.x / videoWidth) * 100,
        y2: (b.y / videoHeight) * 100,
      };

      if (res.queued) {
        notifyNotificationsChanged();
        setStatus({ type: 'success', message: res.message || 'Вектор добавлен в очередь на сохранение' });
        finish(vector);
        return;
      }

      setSavedVector(vector);
      setStatus({ type: 'success', message: res.message || 'Вектор направления сохранён' });
      finish(vector);
    } catch (error: any) {
      setStatus({ type: 'error', message: error.message || 'Ошибка при сохранении вектора' });
    }
  };

  const handleDelete = async () => {
    if (!camera.camera_id) return;
    if (barrierOpenOn === 'entry' || barrierOpenOn === 'exit') return;
    if (!window.confirm('Сбросить вектор? Направление перестанет определяться, проезды будут логироваться без него.')) {
      return;
    }

    setStatus({ type: 'loading', message: 'Сброс вектора...' });

    try {
      const res = await camerasAPI.deleteDirectionVector(camera.camera_id);
      setSavedVector(null);
      setLineState((prev) => ({ ...prev, isDrawing: false, line: null }));
      if (res.queued) {
        notifyNotificationsChanged();
      }
      setStatus({ type: 'success', message: res.message || 'Вектор направления сброшен' });
      finish(null);
    } catch (error: any) {
      setStatus({ type: 'error', message: error.message || 'Ошибка при сбросе вектора' });
    }
  };

  const handleClearSelection = () => {
    setLineState((prev) => ({ ...prev, isDrawing: false, line: null }));
  };

  // Сброс вектора запрещён, пока шлагбаум открывается только на въезд/выезд
  const barrierBlocksReset = barrierOpenOn === 'entry' || barrierOpenOn === 'exit';
  const barrierBlockHint =
    barrierOpenOn === 'entry'
      ? 'Шлагбаум открывается только на въезд — сначала переключите его на «На любой проезд»'
      : 'Шлагбаум открывается только на выезд — сначала переключите его на «На любой проезд»';

  const canvasEl = canvasRef.current;
  const coords =
    lineState.line && canvasEl
      ? {
          a: canvasToVideo(lineState.line.x1, lineState.line.y1, canvasEl),
          b: canvasToVideo(lineState.line.x2, lineState.line.y2, canvasEl),
        }
      : null;

  return (
    <div className="roi-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="roi-modal-content">
        <div className="roi-modal-header">
          <h2>
            <img src={vectorIconSvgUrl} alt="" className="roi-modal-title-icon" />
            Вектор направления въезда
          </h2>
          <button className="roi-modal-close" onClick={onClose} type="button">×</button>
        </div>

        <div className="roi-controls">
          <div className="roi-instruction">
            Проведите линию по видео от точки, где автомобиль появляется, к точке, куда он едет при въезде.
            Стрелка укажет направление «въезд». Проезд в противоположную сторону будет считаться выездом.
          </div>
          <div className="roi-instruction">
            <strong>Если у вас отдельные камеры на въезд и выезд</strong> — вектор рисовать не нужно.
            <br />
            <strong>Одна камера на оба направления</strong> — нарисуйте направление въезда, иначе система
            не отличит въезд от выезда.
          </div>
          {barrierBlocksReset && (
            <div className="roi-warning">{barrierBlockHint}.</div>
          )}
        </div>

        {camera.webrtc_out ? (
          <div className="roi-video-container" ref={containerRef}>
            <canvas ref={canvasRef} className="roi-canvas-overlay direction-vector-canvas" />
          </div>
        ) : (
          <div className="roi-no-video">Видео поток недоступен для выбора вектора</div>
        )}

        {coords && (
          <div className="roi-info">
            <strong>Новый вектор:</strong><br />
            Начало: ({coords.a.x}, {coords.a.y}) → Конец: ({coords.b.x}, {coords.b.y})
          </div>
        )}
        {!coords && savedVector && (
          <div className="roi-info">
            <strong>Текущий вектор:</strong><br />
            ({savedVector.x1.toFixed(1)}%, {savedVector.y1.toFixed(1)}%) → ({savedVector.x2.toFixed(1)}%,{' '}
            {savedVector.y2.toFixed(1)}%)
          </div>
        )}

        {status.type && (
          <div className={`roi-status roi-status--${status.type}`}>{status.message}</div>
        )}

        <div className="roi-buttons">
          <button
            className="roi-btn roi-btn--primary"
            onClick={handleSave}
            disabled={!lineState.line || status.type === 'loading'}
            type="button"
          >
            <span className="roi-btn__inner">
              <img src={btnIcon.save} alt="" className="roi-btn__icon" width={20} height={20} />
              Сохранить вектор
            </span>
          </button>
          <button className="roi-btn roi-btn--secondary" onClick={handleClearSelection} type="button">
            <span className="roi-btn__inner">
              <img src={btnIcon.clear} alt="" className="roi-btn__icon" width={20} height={20} />
              Очистить выбор
            </span>
          </button>
          <button
            className="roi-btn roi-btn--secondary"
            onClick={handleDelete}
            disabled={!savedVector || status.type === 'loading' || barrierBlocksReset}
            title={barrierBlocksReset ? barrierBlockHint : undefined}
            type="button"
          >
            <span className="roi-btn__inner">
              <img src={btnIcon.sbros} alt="" className="roi-btn__icon" width={20} height={20} />
              Сбросить вектор
            </span>
          </button>
          <button className="roi-btn roi-btn--secondary" onClick={onClose} type="button">
            <span className="roi-btn__inner">
              <img src={btnIcon.cancel} alt="" className="roi-btn__icon" width={18} height={18} />
              Отмена
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DirectionVectorSelector;
